import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  fetchInscription,
  fetchPaymentConfig,
  payerInscription,
  reconcileInscriptionTransaction,
} from '../../services/academiaApprenantService';
import { fetchWalletPaymentPreview } from '../../services/walletService';
import { buildReferralQrImageUrl } from '../../services/parrainageService';
import { formatMoney } from '../../utils/currency';
import {
  inscriptionFraisInscriptionDue,
  inscriptionResteAPayer,
} from '../../utils/inscriptionPresentation';
import {
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_MAX_ATTEMPTS,
  pollPaymentStatus,
} from '../../utils/paymentPolling';

const MODES = [
  { value: 'MOBILE_MONEY', label: 'Mobile Money', subtitle: 'M-Pesa / Orange / Airtel', icon: 'phone_iphone' },
  // { value: 'CARTE_BANCAIRE', label: 'Carte bancaire', subtitle: 'Via FlexPay (confirmation mobile)', icon: 'credit_card' },
  // { value: 'VIREMENT_BANCAIRE', label: 'Virement bancaire', subtitle: 'Validation sous 48h ouvrées', icon: 'account_balance' },
  { value: 'PORTEFEUILLE', label: 'Portefeuille', subtitle: 'Solde JCONSULT MY Academia', icon: 'account_balance_wallet' },
] as const;

type ModePaiement =
  | (typeof MODES)[number]['value']
  | 'CARTE_BANCAIRE'
  | 'VIREMENT_BANCAIRE';

type ResultStatus = 'SUCCESS' | 'FAILED' | 'PENDING' | 'TIMEOUT';

const POLL_TOTAL_SEC = Math.ceil((PAYMENT_POLL_MAX_ATTEMPTS * PAYMENT_POLL_INTERVAL_MS) / 1000);

export default function ApprenantPaiementPanel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state as {
    montant?: number;
    maxAmount?: number;
    focusInscription?: boolean;
    allowCustomAmount?: boolean;
    paymentChoice?: 'inscription' | 'total' | 'advance';
    filiereLabel?: string;
  } | null);
  const montantState = routeState?.montant;
  const maxAmountState = routeState?.maxAmount;
  const focusInscription = Boolean(routeState?.focusInscription);
  const allowCustomAmount = Boolean(routeState?.allowCustomAmount);
  const paymentChoice = routeState?.paymentChoice;
  const filiereLabel = routeState?.filiereLabel?.trim() ?? '';
  const pollAbortRef = useRef<AbortController | null>(null);

  const [amount, setAmount] = useState<number>(montantState ?? 0);
  const [maxAmount, setMaxAmount] = useState<number>(maxAmountState ?? montantState ?? 0);
  const [loadingIns, setLoadingIns] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ModePaiement>('MOBILE_MONEY');
  const [phase, setPhase] = useState<'choisir' | 'payer'>('choisir');
  const [phone, setPhone] = useState('243');
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [liveStatus, setLiveStatus] = useState('EN_ATTENTE');
  const [pollAttempt, setPollAttempt] = useState(0);
  const [bankConfig, setBankConfig] = useState<Record<string, string> | null>(null);
  const [paymentQrUrl, setPaymentQrUrl] = useState<string | null>(null);
  const [virementRef, setVirementRef] = useState('');
  const [virementTitulaire, setVirementTitulaire] = useState('');
  const [appliquerReductionPortefeuille, setAppliquerReductionPortefeuille] = useState(true);
  const [walletPreview, setWalletPreview] = useState<{
    soldeDisponible?: number;
    reductionPortefeuille?: number;
    montantRestant?: number;
    couvertureComplete?: boolean;
    devise?: string;
  } | null>(null);

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const ins = await fetchInscription(id);
        if (cancelled) return;
        const reste = inscriptionResteAPayer(ins);
        const fraisInscription = inscriptionFraisInscriptionDue(ins);
        const cap = maxAmountState != null && maxAmountState > 0 ? maxAmountState : reste;
        setMaxAmount(cap);
        if (montantState != null && montantState > 0) {
          setAmount(Math.min(montantState, cap));
        } else if (focusInscription && fraisInscription > 0.01) {
          setAmount(fraisInscription);
        } else if (!focusInscription) {
          setAmount(reste);
        } else {
          setAmount(fraisInscription > 0.01 ? fraisInscription : reste);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Inscription introuvable');
      } finally {
        if (!cancelled) setLoadingIns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, montantState, maxAmountState, focusInscription]);

  useEffect(() => {
    void fetchPaymentConfig().then(setBankConfig).catch(() => setBankConfig(null));
  }, []);

  useEffect(() => {
    if (mode === 'PORTEFEUILLE' || loadingIns) {
      setWalletPreview(null);
      return;
    }
    const cap = maxAmount > 0 ? maxAmount : amount;
    const payAmount = Math.min(Math.max(amount, 0), cap);
    if (payAmount <= 0) {
      setWalletPreview(null);
      return;
    }
    let cancelled = false;
    void fetchWalletPaymentPreview(payAmount)
      .then((preview) => {
        if (!cancelled) setWalletPreview(preview);
      })
      .catch(() => {
        if (!cancelled) setWalletPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [amount, maxAmount, mode, loadingIns]);

  const needsPhone = mode === 'MOBILE_MONEY' || mode === 'CARTE_BANCAIRE';

  const goResult = useCallback(
    (status: ResultStatus, reference = '', message = '', paidAmount?: number) => {
      const q = new URLSearchParams({
        status,
        inscriptionId: id ?? '',
        amount: String(paidAmount ?? amount),
        reference,
        paymentMode: mode,
      });
      if (message) q.set('message', message);
      navigate(`/app/paiement/resultat?${q.toString()}`);
    },
    [amount, id, mode, navigate],
  );

  const stopPolling = useCallback(() => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    setPolling(false);
    setPaying(false);
  }, []);

  const handleCancelPolling = () => {
    stopPolling();
    setError('Paiement annulé. Vous pouvez réessayer lorsque vous êtes prêt.');
  };

  const clampAmount = (value: number) => {
    const cap = maxAmount > 0 ? maxAmount : value;
    return Math.min(Math.max(value, 0), cap);
  };

  const handleAmountInput = (raw: string) => {
    const n = Number(raw.replace(/\s/g, ''));
    if (!Number.isFinite(n)) {
      setAmount(0);
      return;
    }
    setAmount(clampAmount(n));
  };

  const paymentTitle =
    paymentChoice === 'advance'
      ? 'Paiement — avance'
      : paymentChoice === 'total'
        ? 'Paiement — totalité'
        : focusInscription
          ? "Frais d'inscription"
          : 'Paiement';

  const paymentDescription =
    paymentChoice === 'advance'
      ? filiereLabel
        ? `Avance pour « ${filiereLabel} ». Vous pourrez compléter le solde plus tard.`
        : 'Versement partiel sur votre formation. Le solde reste payable depuis votre fiche inscription.'
      : paymentChoice === 'total'
        ? filiereLabel
          ? `Règlement du solde total pour « ${filiereLabel} ».`
          : 'Règlement de la totalité du solde formation.'
        : focusInscription
          ? filiereLabel
            ? `Réglez les frais d'inscription pour « ${filiereLabel} ».`
            : "Réglez les frais d'inscription de votre souscription à la formation."
          : 'Choisissez votre mode de paiement et confirmez le règlement.';

  const handlePay = async () => {
    if (!id?.trim()) {
      setError('Inscription introuvable.');
      return;
    }
    const payAmount = clampAmount(amount);
    if (payAmount <= 0) {
      setError('Indiquez un montant supérieur à zéro.');
      return;
    }
    if (maxAmount > 0 && payAmount > maxAmount + 0.01) {
      setError(`Le montant ne peut pas dépasser ${formatMoney(maxAmount, 'CDF')}.`);
      return;
    }
    if (needsPhone && phone.replace(/\D/g, '').length < 11) {
      setError('Numéro de téléphone invalide (format 243…).');
      return;
    }

    setPaying(true);
    setError(null);
    pollAbortRef.current?.abort();
    const abort = new AbortController();
    pollAbortRef.current = abort;

    try {
      const result = await payerInscription(id, {
        modePaiement: mode,
        montantCdf: payAmount,
        telephonePayeur: needsPhone ? phone : undefined,
        appliquerReductionPortefeuille:
          mode !== 'PORTEFEUILLE' && appliquerReductionPortefeuille,
        paymentDetails:
          mode === 'VIREMENT_BANCAIRE'
            ? {
                virementTitulaireEmetteur: virementTitulaire,
                virementReferenceCommunication: virementRef || undefined,
              }
            : undefined,
      });
      if (result?.paymentQrUrl) setPaymentQrUrl(String(result.paymentQrUrl));
      const tx = result?.transaction;
      const txId = tx?.id;
      const reference = tx?.reference ?? '';

      if (mode === 'VIREMENT_BANCAIRE') {
        goResult(
          'PENDING',
          reference,
          result?.message ??
            'Virement enregistré. Effectuez le paiement avec la référence indiquée ; vous recevrez une notification à validation.',
          payAmount,
        );
        return;
      }

      if (mode === 'PORTEFEUILLE') {
        const st = (tx?.statut ?? '').toUpperCase();
        if (st === 'VALIDEE') {
          goResult(
            'SUCCESS',
            reference,
            'Votre paiement par portefeuille a été enregistré avec succès.',
            payAmount,
          );
        } else if (st === 'ECHOUEE' || st === 'ANNULEE') {
          goResult(
            'FAILED',
            reference,
            'Le débit portefeuille a échoué. Vérifiez votre solde ou choisissez Mobile Money.',
            payAmount,
          );
        } else {
          goResult('PENDING', reference, 'Paiement portefeuille en cours de traitement.', payAmount);
        }
        return;
      }

      if (!txId) {
        goResult(
          'PENDING',
          reference,
          'Demande envoyée. La confirmation peut prendre quelques minutes.',
          payAmount,
        );
        return;
      }

      if (mode !== 'MOBILE_MONEY' && mode !== 'CARTE_BANCAIRE') {
        goResult('PENDING', reference, '', payAmount);
        return;
      }

      setPolling(true);
      setLiveStatus('EN_ATTENTE');
      setPollAttempt(0);

      const finalStatus = await pollPaymentStatus({
        fetchStatus: () => reconcileInscriptionTransaction(txId),
        onStatus: setLiveStatus,
        onAttempt: (attempt) => setPollAttempt(attempt),
        signal: abort.signal,
        successStatuses: ['VALIDEE'],
        failureStatuses: ['ECHOUEE', 'ANNULEE'],
      });

      if (abort.signal.aborted || finalStatus === 'CANCELLED') {
        return;
      }

      if (finalStatus === 'VALIDEE') {
        goResult(
          'SUCCESS',
          reference,
          'Paiement confirmé. Merci — votre inscription sera mise à jour sous peu.',
          payAmount,
        );
      } else if (finalStatus === 'ECHOUEE' || finalStatus === 'ANNULEE') {
        goResult(
          'FAILED',
          reference,
          'Le paiement Mobile Money a été refusé ou annulé. Réessayez ou utilisez un autre numéro.',
          payAmount,
        );
      } else if (finalStatus === 'TIMEOUT') {
        goResult(
          'TIMEOUT',
          reference,
          `Délai dépassé (${Math.floor(POLL_TOTAL_SEC / 60)} min). Validez la demande sur votre téléphone puis consultez votre inscription, ou réessayez le paiement.`,
          payAmount,
        );
      } else {
        goResult(
          'PENDING',
          reference,
          'Confirmation toujours en attente. Consultez votre inscription dans quelques minutes.',
          payAmount,
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Le paiement a échoué.';
      setError(msg);
    } finally {
      setPolling(false);
      setPaying(false);
      pollAbortRef.current = null;
    }
  };

  const pollProgressPct =
    pollAttempt > 0 ? Math.min(100, Math.round((pollAttempt / PAYMENT_POLL_MAX_ATTEMPTS) * 100)) : 0;
  const pollRemainingSec = Math.max(
    0,
    Math.ceil(((PAYMENT_POLL_MAX_ATTEMPTS - pollAttempt) * PAYMENT_POLL_INTERVAL_MS) / 1000),
  );

  return (
    <ApprenantPageShell
      title={paymentTitle}
      description={paymentDescription}
      sideCard={{
        label:
          paymentChoice === 'advance'
            ? 'Avance'
            : focusInscription
              ? "Frais d'inscription"
              : 'Montant à régler',
        value: loadingIns ? '…' : formatMoney(amount, 'CDF'),
        helper:
          maxAmount > 0 && amount < maxAmount - 0.01
            ? `Solde après paiement ~ ${formatMoney(maxAmount - amount, 'CDF')}`
            : MODES.find((m) => m.value === mode)?.label ?? 'Mobile Money',
        sideLinks: id
          ? [{ label: 'Détail inscription', to: `/app/inscription/${id}`, icon: 'visibility' }]
          : [],
      }}
      isLoading={loadingIns}
      loadingMessage="Chargement du paiement…"
    >
      <p className="text-xs font-bold text-primary">
        {id ? <Link to={`/app/inscription/${id}`}>← Détail inscription</Link> : null}
      </p>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <p className="font-bold">Échec du paiement</p>
          <p className="mt-1">{error}</p>
        </div>
      ) : null}

      {focusInscription ? (
        <div className="rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-slate-800">
          <p className="font-bold text-primary">Frais d&apos;inscription à la formation</p>
          <p className="mt-1 text-on-surface-variant">
            Ce paiement concerne uniquement votre souscription (frais d&apos;inscription). Les frais de
            participation et de brevet seront réglés séparément depuis votre fiche inscription, une fois cette
            étape validée.
          </p>
        </div>
      ) : null}

      {paymentChoice === 'total' ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-slate-800">
          <p className="font-bold text-emerald-800">Paiement de la totalité</p>
          <p className="mt-1 text-on-surface-variant">
            Vous réglez l&apos;intégralité du solde restant. Les montants sont répartis sur les lignes de frais
            (inscription, participation, brevet) selon les règles de l&apos;établissement.
          </p>
        </div>
      ) : null}

      {allowCustomAmount || paymentChoice === 'advance' ? (
        <div className="rounded-xl border border-outline-variant/20 bg-white px-4 py-3 shadow-sm">
          <label className="block text-sm">
            <span className="font-bold text-slate-900">Montant à payer (CDF)</span>
            <input
              type="number"
              min={1}
              max={maxAmount > 0 ? maxAmount : undefined}
              step={1000}
              className="mt-2 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm font-semibold tabular-nums"
              value={amount > 0 ? amount : ''}
              onChange={(e) => handleAmountInput(e.target.value)}
              disabled={paying || polling}
            />
          </label>
          {maxAmount > 0 ? (
            <p className="mt-1 text-xs text-on-surface-variant">
              Maximum : {formatMoney(maxAmount, 'CDF')}
            </p>
          ) : null}
        </div>
      ) : null}

      {phase === 'choisir' ? (
        <div className="space-y-3">
          {MODES.map((m) => {
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                disabled={paying || polling}
                className={[
                  'flex w-full items-center gap-4 rounded-xl border-2 bg-white p-4 text-left transition',
                  active ? 'border-primary shadow-sm' : 'border-outline-variant/15 hover:border-primary/30',
                  paying || polling ? 'opacity-60' : '',
                ].join(' ')}
              >
                <LucideSymbolIcon name={m.icon} tone="primary" size={24} />
                <div className="flex-1">
                  <p className="font-bold text-slate-900">{m.label}</p>
                  <p className="text-xs text-on-surface-variant">{m.subtitle}</p>
                </div>
                {active ? <LucideSymbolIcon name="check_circle" tone="primary" size={22} /> : null}
              </button>
            );
          })}
          <button
            type="button"
            disabled={paying || polling}
            onClick={() => setPhase('payer')}
            className="mt-4 w-full rounded-xl bg-primary py-3 text-sm font-bold text-white disabled:opacity-50 sm:w-auto sm:px-8"
          >
            Continuer
          </button>
        </div>
      ) : (
        <div className="space-y-4 rounded-2xl border border-outline-variant/15 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold text-on-surface-variant">
            Mode : {MODES.find((m) => m.value === mode)?.label}
          </p>
          {mode !== 'PORTEFEUILLE' &&
          walletPreview &&
          (walletPreview.reductionPortefeuille ?? 0) > 0 ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={appliquerReductionPortefeuille}
                  onChange={(e) => setAppliquerReductionPortefeuille(e.target.checked)}
                  disabled={paying || polling}
                />
                <span>
                  <span className="font-bold text-emerald-900">Réduction automatique portefeuille</span>
                  <span className="mt-1 block text-emerald-800">
                    Solde : {formatMoney(walletPreview.soldeDisponible ?? 0, walletPreview.devise ?? 'CDF')} —{' '}
                    {appliquerReductionPortefeuille
                      ? `−${formatMoney(walletPreview.reductionPortefeuille ?? 0, walletPreview.devise ?? 'CDF')} → reste ${formatMoney(walletPreview.montantRestant ?? 0, walletPreview.devise ?? 'CDF')}`
                      : 'désactivée pour ce paiement'}
                  </span>
                </span>
              </label>
            </div>
          ) : null}
          {needsPhone ? (
            <label className="block text-sm">
              <span className="font-semibold text-slate-800">Téléphone payeur</span>
              <input
                type="tel"
                className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="243900000000"
                disabled={paying || polling}
              />
            </label>
          ) : null}
          {mode === 'VIREMENT_BANCAIRE' && bankConfig ? (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">
              <p className="font-bold text-slate-900">Coordonnées bancaires</p>
              <p className="mt-2">Bénéficiaire : {bankConfig.bankBeneficiary}</p>
              <p>Banque : {bankConfig.bankName}</p>
              <p>Compte / IBAN : {bankConfig.bankIbanOrAccount}</p>
              {bankConfig.bankBic ? <p>BIC : {bankConfig.bankBic}</p> : null}
              <label className="mt-3 block">
                <span className="text-xs font-semibold">Votre nom (émetteur)</span>
                <input
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                  value={virementTitulaire}
                  onChange={(e) => setVirementTitulaire(e.target.value)}
                />
              </label>
              <label className="mt-2 block">
                <span className="text-xs font-semibold">Référence de communication (optionnel)</span>
                <input
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                  value={virementRef}
                  onChange={(e) => setVirementRef(e.target.value)}
                />
              </label>
            </div>
          ) : null}
          {mode === 'PORTEFEUILLE' ? (
            <p className="text-sm text-on-surface-variant">
              Le montant sera débité de votre portefeuille JCONSULT MY Academia.
            </p>
          ) : null}
          {paymentQrUrl ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-outline-variant/30 p-4">
              <p className="text-xs font-bold text-on-surface-variant">QR code de paiement</p>
              <img
                src={buildReferralQrImageUrl(paymentQrUrl, 180)}
                alt="QR paiement"
                className="h-44 w-44 rounded-lg"
              />
              <p className="break-all text-center text-[10px] text-on-surface-variant">{paymentQrUrl}</p>
            </div>
          ) : null}
          <button
            type="button"
            disabled={paying || polling}
            onClick={() => void handlePay()}
            className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-white disabled:opacity-50 sm:w-auto sm:px-8"
          >
            {paying || polling ? 'Traitement…' : 'Confirmer le paiement'}
          </button>
          <button
            type="button"
            className="text-sm font-bold text-primary disabled:opacity-50"
            disabled={paying || polling}
            onClick={() => setPhase('choisir')}
          >
            Changer le mode de paiement
          </button>
        </div>
      )}

      {polling ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="polling-title"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p id="polling-title" className="mt-4 text-center text-lg font-bold text-slate-900">
              Confirmation {mode === 'CARTE_BANCAIRE' ? 'carte / Mobile Money' : 'Mobile Money'}
            </p>
            <p className="mt-2 text-center text-sm text-on-surface-variant">
              Validez la demande sur votre téléphone.
            </p>
            <p className="mt-1 text-center text-xs font-semibold text-primary">
              Statut : {liveStatus}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${pollProgressPct}%` }}
              />
            </div>
            <p className="mt-2 text-center text-xs text-on-surface-variant">
              Temps restant estimé : {pollRemainingSec > 0 ? `${pollRemainingSec} s` : 'fin…'}
            </p>
            <button
              type="button"
              className="mt-6 w-full rounded-xl border border-outline-variant/30 py-2.5 text-sm font-bold text-slate-700"
              onClick={handleCancelPolling}
            >
              Annuler et revenir
            </button>
          </div>
        </div>
      ) : null}
    </ApprenantPageShell>
  );
}
